import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";

import { InputLabel } from "../../../../components/inputs/InputLabel";
import { LogReminderPayload } from "../../../../types/onboarding";

interface LogReminderDialogProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (payload: LogReminderPayload) => Promise<boolean>;
}

const CHANNEL_OPTIONS = [
  { value: "EMAIL", label: "Email" },
  { value: "PHONE", label: "Phone" },
];

/**
 * US4: log that a reminder was sent to the prospect (channel + optional note).
 *
 * Nothing leaves the Back Office here. The label reads like a send, so the
 * dialog says plainly that the agent sends the reminder themselves and records
 * it afterwards — otherwise a customer who was never contacted ends up with a
 * "Reminder sent" entry against their name.
 */
const LogReminderDialog: React.FC<LogReminderDialogProps> = ({
  visible,
  onHide,
  onSubmit,
}) => {
  const [channel, setChannel] = useState("EMAIL");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) {
      setChannel("EMAIL");
      setNote("");
      setError(null);
      setSubmitting(false);
    }
  }, [visible]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const ok = await onSubmit({ channel, note: note.trim() || null });
      if (ok) onHide();
    } catch (e) {
      setError("The reminder could not be logged. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const footer = (
    <div>
      <Button label="Cancel" text onClick={onHide} disabled={submitting} />
      <Button label="Log reminder" icon="pi pi-check" onClick={submit} loading={submitting} />
    </div>
  );

  return (
    <Dialog
      header="Log reminder"
      visible={visible}
      onHide={onHide}
      footer={footer}
      style={{ width: "32rem" }}
    >
      {error && <Message severity="error" className="w-full mb-3" text={error} />}
      <Message
        severity="info"
        className="w-full mb-3"
        text="No email is sent from here. Contact the customer yourself — by email or phone — then record it below, so the next person can see it has been chased and when."
      />
      <div className="field">
        <InputLabel name="reminder_channel" label="Channel" />
        <Dropdown
          inputId="reminder_channel"
          value={channel}
          options={CHANNEL_OPTIONS}
          optionLabel="label"
          optionValue="value"
          onChange={(e) => setChannel(e.value)}
          className="w-full"
        />
      </div>
      <div className="field">
        <InputLabel name="reminder_note" label="Note (optional)" />
        <InputTextarea
          id="reminder_note"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          className="w-full"
        />
      </div>
    </Dialog>
  );
};

export default LogReminderDialog;
